import $ from 'jquery';
import React, {useRef} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import TFIActions from 'notebook/tfi/TFIActions';
import {AccountType, TFIAccount} from 'notebook/tfi/TFIConstants';
import TFIIcon from 'notebook/tfi/TFIIcon';
import TFIUtil from 'notebook/tfi/TFIUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

const BOX_OAUTH_URL = '/ext/tfi/box-oauth/start';
const DROPBOX_OAUTH_URL = '/ext/tfi/dropbox-oauth/start';
const EGNYTE_OAUTH_URL = '/ext/tfi/egnyte-oauth/start';
const GDRIVE_OAUTH_URL = '/ext/tfi/gdrive-oauth/start';
const ONEDRIVE_OAUTH_URL = '/ext/tfi/onedrive-oauth/start';

interface AddAccountRowProps {
  accountType: AccountType;
  connectedCount?: number | null;
  onRequestClose: () => void;
}

interface State {
  eventNamespace: string;
  oauthWindow?: Window | null;
}

interface TFIData {
  account?: TFIAccount;
  message: string;
  severity: 'WARN' | 'SUCCESS' | 'ERROR';
}

interface TFIMessageData {
  tfiData?: TFIData;
}

export class AddAccountRow extends React.Component<AddAccountRowProps, State> {
  override state: State = {
    eventNamespace: _.uniqueId('tfiOAuth-'),
  };

  override componentDidMount(): void {
    // Listen for response from oauth flow
    $(window).on(`message.${this.state.eventNamespace}`, (e) => {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'data' does not exist on type ... Remove this comment to see the full error message
      const {tfiData} = e.originalEvent.data as TFIMessageData;
      // Ensure that the message is for TFI oauth
      if (tfiData === undefined) {
        return;
      }

      // @ts-expect-error ts-migrate(2339) FIXME: Property 'oauthWindow' does not exist on type '{ e... Remove this comment to see the full error message
      if (this.state.oauthWindow == null || e.originalEvent.source !== this.state.oauthWindow) {
        // Doesn't match our opened window, and should be closed
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'source' does not exist on type ... Remove this comment to see the full error message
        if (e.originalEvent.source != null) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'source' does not exist on type ... Remove this comment to see the full error message
          e.originalEvent.source.close();
        }
        return;
      }

      const {account, message, severity} = tfiData;
      if (message != null) {
        switch (severity) {
          case 'SUCCESS':
            notifications.success(message);
            TFIActions.addAccount(account!);
            analytics.track('tfi', 'added_account', {accountType: account?.accountType});
            break;
          case 'WARN':
            notifications.warn(message);
            break;
          case 'ERROR':
            notifications.error(message);
            break;
          default:
            break;
        }
      }

      this.state.oauthWindow.close();
      this.props.onRequestClose();
    });
  }

  override componentWillUnmount(): void {
    $(window).off(`message.${this.state.eventNamespace}`);
  }

  _connectAccount = (): void => {
    const oauthURL = ((): string | undefined => {
      switch (this.props.accountType) {
        case AccountType.BOX:
          return BOX_OAUTH_URL;
        case AccountType.DROPBOX:
          return DROPBOX_OAUTH_URL;
        case AccountType.EGNYTE:
          return EGNYTE_OAUTH_URL;
        case AccountType.GDRIVE:
          return GDRIVE_OAUTH_URL;
        case AccountType.ONEDRIVE:
          return ONEDRIVE_OAUTH_URL;
        default:
          return undefined;
      }
    })();
    const oauthWindow = window.open(oauthURL, '_blank', 'height=500,width=600');
    this.setState({oauthWindow});
  };

  override render(): JSX.Element {
    return (
      <div
        className="tfi-addAccountRow"
        role="listitem"
        {...getTestProps({component: 'AddAccountRow', element: 'root', key: this.props.accountType})}
      >
        <TFIIcon accountType={this.props.accountType} />
        <div className="tfi-addAccountRow-label">
          <div>{TFIUtil.readableAccountType(this.props.accountType)}</div>
          <div className="tfi-addAccountRow-count">
            {(() => {
              if (this.props.connectedCount != null) {
                // Null connected count means data is still loading
                const displayedCount = this.props.connectedCount > 0 ? this.props.connectedCount : 'No';
                return `${displayedCount} ${pluralize(this.props.connectedCount, 'account')} connected`;
              }
              return null;
            })()}
          </div>
        </div>
        <div className="pull-right">
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            onClick={this._connectAccount}
            size={ButtonSize.SMALL}
            text="Connect"
          />
        </div>
      </div>
    );
  }
}

interface TFIAddAccountModalProps {
  tfiAccounts?: Array<TFIAccount> | null;
  onRequestClose: () => void;
}

function TFIAddAccountModal(props: TFIAddAccountModalProps): JSX.Element {
  const inputRef = useRef(null);
  return (
    <Modal
      ref={inputRef}
      title="Add account"
      onRequestClose={props.onRequestClose}
      buttons={[{text: 'Done', submit: true, onClick: props.onRequestClose, style: ButtonStyle.PRIMARY}]}
    >
      <div aria-label="Available account types" role="list">
        {(() => {
          const result = [];
          for (const accountType of Array.from(TFIUtil.getAllowedAccountTypes())) {
            let connectedCount: number | null;
            if (props.tfiAccounts != null) {
              connectedCount = _.where(props.tfiAccounts, {accountType}).length;
            } else {
              connectedCount = null; // For when tfi accounts are still loading
            }
            result.push(
              <AddAccountRow
                key={accountType}
                accountType={accountType}
                connectedCount={connectedCount}
                onRequestClose={props.onRequestClose}
              />
            );
          }
          return result;
        })()}
      </div>
    </Modal>
  );
}

export default TFIAddAccountModal;
